import { useEffect, useState, useCallback } from 'react';
import { Button, Flex, Text } from 'src/components/ui';
import { FormProvider, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { StringInputHook } from '../common/form';
import AlertMessage from '../common/AlertMessage';
import { PasswordSchema, IPasswordForm } from 'src/lib/schemas';
import authService from 'src/services/auth';
import { OtpVerification } from './OtpVerification';

const SetPasswordForm = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [isPasswordVisible, setIsPasswordVisible] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [searchParams] = useSearchParams();
	const [isPhoneInvite, setIsPhoneInvite] = useState(false);
	const [phoneNumber, setPhoneNumber] = useState('');
	const { executeRecaptcha } = useGoogleReCaptcha();

	const getCaptchaToken = useCallback(async (action: string): Promise<string | undefined> => {
		if (!executeRecaptcha) {
			console.warn('reCAPTCHA not ready');
			return undefined;
		}
		try {
			return await executeRecaptcha(action);
		} catch (err) {
			console.error('reCAPTCHA execution failed:', err);
			return undefined;
		}
	}, [executeRecaptcha]);

	useEffect(() => {
		const token = searchParams.get('token');
		if (!token) {
			setError('Token is invalid or expired');
		}
	}, []);

	const formMethods = useForm<IPasswordForm>({
		resolver: zodResolver(PasswordSchema),
	});

	const onSubmit = formMethods.handleSubmit(async (formData) => {
		setError(null);
		setIsLoading(true);
		const token = searchParams.get('token');
		if (!token) {
			setError('Token is invalid or expired');
			setIsLoading(false);
			return;
		}
		try {
			await authService.acceptInvitation(token, formData.password);
			window.location.href = '/home';
		} catch (error: any) {
			const { message } = error.response?.data || error;
			setError(message);
			setIsLoading(false);
		}
		formMethods.reset({ password: '', confirmPassword: '' });
	});

	const handleOtpVerify = async (otp: string) => {
		setError(null);
		setIsLoading(true);
		const token = searchParams.get('token');
		if (!token) {
			setError('Token is invalid or expired');
			setIsLoading(false);
			return;
		}
		try {
			await authService.acceptInvitation(token, undefined, otp);
			window.location.href = '/home';
		} catch (error: any) {
			const { message } = error.response?.data || error;
			setError(message);
			setIsLoading(false);
		}
	};

	const handleResendOtp = async () => {
		if (phoneNumber) {
			setError(null);
			try {
				const captchaToken = await getCaptchaToken('resend_otp');
				await authService.sendOtp(phoneNumber, captchaToken);
			} catch (error: any) {
				setError(error?.response?.data?.message || 'Failed to resend OTP');
			}
		}
	};

	// If phone invite, show OTP verification
	if (isPhoneInvite && phoneNumber) {
		return (
			<Flex direction="column" gap={3}>
				{error && <AlertMessage status="error">{error}</AlertMessage>}
				<OtpVerification
					phone={phoneNumber}
					onVerify={handleOtpVerify}
					onResend={handleResendOtp}
					isLoading={isLoading}
				/>
			</Flex>
		);
	}

	// Default: password form
	return (
		<FormProvider {...formMethods}>
			{error && <AlertMessage status="error">{error}</AlertMessage>}
			<form onSubmit={onSubmit}>
				<Flex direction="column" gap="28px">
					<StringInputHook
						label="Password"
						name="password"
						type={isPasswordVisible ? 'text' : 'password'}
						placeholder="Strong password"
						required
						inputProps={{ size: 'md', fontSize: '14px' }}
					/>
					<Flex direction="column" gap="3">
						<StringInputHook
							label="Confirm Password"
							name="confirmPassword"
							type={isPasswordVisible ? 'text' : 'password'}
							placeholder="Strong password"
							required
							inputProps={{ size: 'md', fontSize: '14px' }}
						/>
						<Flex alignItems="center">
							<input
								type="checkbox"
								name="showPassword"
								id="showPassword"
								onChange={() => setIsPasswordVisible(!isPasswordVisible)}
							/>
							<label htmlFor="showPassword">
								<Text fontSize="13px">&nbsp; Show password</Text>
							</label>
						</Flex>
					</Flex>
					<Button
						bg="primary"
						color="white"
						type="submit"
						loadingText="Confirming..."
						isLoading={isLoading}
						_hover={{ bg: '#d93f09' }}
						_active={{ bg: '#c43808' }}
					>
						Confirm
					</Button>
				</Flex>
			</form>
		</FormProvider>
	);
};
export default SetPasswordForm;
